/**
 * Hair Quiz Utilities - Shared functions for newsletter and results pages
 */
window.hairQuizUtils = (function () {
  'use strict';

  const SHOPIFY_API_BASE = '/products';

  // Fixed category order for product slides (same as hair-quiz-results.js)
  const PRODUCT_CATEGORY_KEYS = [
    'shampoo',
    'conditioner',
    'treatment',
    'primer',
    'styler',
    'holder',
  ];

  /**
   * Build category mapping based on number of products
   * If <= 5 products, Primer is missing (index 3)
   * If 6 products, all categories are present
   * @param {number} productCount - Number of products
   * @returns {Array<number>} Array mapping product index to category index
   */
  function buildCategoryMapping(productCount) {
    if (productCount <= 5) {
      return [0, 1, 2, 4, 5]; // Missing Primer (index 3)
    }
    return [0, 1, 2, 3, 4, 5]; // All 6 categories
  }

  /**
   * Fetch a single product from Shopify
   * @param {string} handle - Product handle
   * @returns {Promise<Object|null>} Product object or null
   */
  async function fetchProduct(handle) {
    try {
      const response = await fetch(`${SHOPIFY_API_BASE}/${handle}.js`);
      if (!response.ok) {
        return null;
      }
      const product = await response.json();
      // Normalize type field
      if (product.type && !product.product_type) {
        product.product_type = product.type;
      }
      return product;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetch multiple products by handles
   * @param {Array<string>} handles - Product handles
   * @returns {Promise<Array<Object>>} Array of product objects
   */
  async function fetchProducts(handles) {
    if (!handles || handles.length === 0) return [];
    const products = await Promise.all(handles.map(fetchProduct));
    return products.filter(p => p !== null);
  }

  /**
   * Map products to their categories based on position
   * @param {Array<Object>} products - Product objects
   * @returns {Array<{sku: string, category: string}>} Array of objects with sku and category
   */
  function mapProductSkusToCategories(products) {
    if (!products || products.length === 0) return [];

    const shopCountry = window.shopCountry || 'US'; // Default to US if not set
    const categoryMapping = buildCategoryMapping(products.length);

    return products
      .map((p, index) => {
        let sku;
        if (shopCountry === 'US' || shopCountry === 'CA') {
          sku = p.id;
        } else {
          sku = p.variants && p.variants[0] ? p.variants[0].sku : null;
        }
        const categoryIndex = categoryMapping[index];
        const category = PRODUCT_CATEGORY_KEYS[categoryIndex] || null;
        return sku && category ? { sku, category } : null;
      })
      .filter(item => item !== null);
  }

  /**
   * Store product data on window for newsletter middleware to access
   * @param {Array<Object>} products - Product objects
   */
  function saveSkuToCategoryMap(products) {
    // Save SKU-category mapping for newsletter middleware
    window.hairQuizSkuCategoryMap = mapProductSkusToCategories(products);
  }

  /**
   * Get URL parameters (handles and tags)
   * @returns {Object} Object containing handles and tags arrays
   */
  function getUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      handles: (urlParams.get('handles') || '').split(',').filter(h => h.trim()),
      tags: (urlParams.get('tags') || '').split(',').filter(t => t.trim()),
    };
  }

  // Public API
  return {
    PRODUCT_CATEGORY_KEYS,
    buildCategoryMapping,
    saveSkuToCategoryMap,
    fetchProducts,
    getUrlParams
  };
})();
